import React, { useEffect } from 'react';
import { subscribe } from 'lib/pusher';

interface LinkingState {
  isLinking: boolean;
  channel: string | null;
  error: string | null;
  complete: boolean;
}

interface UseEntityLinkingParams {
  linkingState: LinkingState;
  setLinkingState: React.Dispatch<React.SetStateAction<LinkingState>>;
  onLinkSuccess?: () => void;
  onClose: () => void;
}

interface CreateAccountState {
  isCreating: boolean;
  channel: string | null;
  events: Array<{ name: string; label: string }>;
  error: string | null;
}

interface UseAccountCreationParams {
  createAccountState: CreateAccountState;
  setCreateAccountState: React.Dispatch<
    React.SetStateAction<CreateAccountState>
  >;
  setShowCreatePanel: React.Dispatch<React.SetStateAction<boolean>>;
  setCreatedAccount: React.Dispatch<
    React.SetStateAction<{
      id: string | null;
      name: string | null;
      showNotification: boolean;
      salesforceId?: string;
    }>
  >;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  handleSearchAccounts: (
    searchValue: string,
    page?: number,
    pageSize?: number
  ) => void;
  searchResults?: {
    currentPayload?: { pageSize?: number };
  };
  DEFAULT_PAGE_SIZE: number;
}

interface DeleteEntityLinkState {
  isDeleting: boolean;
  deleteChannel: string | null;
  deleteEvents: Array<{ name: string; label?: string }>;
  deleteComplete: boolean;
  deleteError: string | null;
}

interface UseDeleteEntityLinkParams {
  deleteState: DeleteEntityLinkState;
  setDeleteState: React.Dispatch<React.SetStateAction<DeleteEntityLinkState>>;
  onDeleteSuccess?: () => void;
  toggleOpenModal: () => void;
}

export const useEntityLinking = ({
  linkingState,
  setLinkingState,
  onLinkSuccess,
  onClose,
}: UseEntityLinkingParams) => {
  // Subscribe to Pusher channel for POST (linking) completion
  useEffect(() => {
    if (!linkingState.channel || !linkingState.isLinking) {
      return;
    }

    const subscription = subscribe(linkingState.channel);

    const handleLinkStatusChange = (data: {
      overall_status?: string;
      details?: string;
    }) => {
      if (data.overall_status === 'processed') {
        setLinkingState((prev) => ({
          ...prev,
          channel: null,
          complete: true,
        }));
      } else if (data.overall_status === 'failed') {
        const errorMessage = 'Failed to link CRM account. Please try again.';
        setLinkingState({
          isLinking: false,
          channel: null,
          error: errorMessage,
          complete: false,
        });
      }
    };

    subscription.bind(
      'crm-workflow-process-status-changed',
      handleLinkStatusChange
    );

    return () => {
      subscription.unbind(
        'crm-workflow-process-status-changed',
        handleLinkStatusChange
      );
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkingState.channel, linkingState.isLinking]);

  // Complete when linking is done
  useEffect(() => {
    if (!linkingState.isLinking) return;

    if (linkingState.complete) {
      setLinkingState({
        isLinking: false,
        channel: null,
        error: null,
        complete: false,
      });
      onLinkSuccess?.();
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkingState.complete, linkingState.isLinking, onLinkSuccess, onClose]);
};

export const useAccountCreation = ({
  createAccountState,
  setCreateAccountState,
  setShowCreatePanel,
  setCreatedAccount,
  setSearchText,
  handleSearchAccounts,
  searchResults,
  DEFAULT_PAGE_SIZE,
}: UseAccountCreationParams) => {
  // Subscribe to Pusher channel for account creation
  useEffect(() => {
    if (!createAccountState.channel || !createAccountState.events.length) {
      return;
    }

    const channel = createAccountState.channel;
    const subscription = subscribe(channel);

    const handleProcessStatusChange = (data: {
      overall_status?: string;
      details?: string;
    }) => {
      if (data.overall_status === 'processed') {
        // Account creation completed - close the panel
        setShowCreatePanel(false);
        setCreateAccountState({
          isCreating: false,
          channel: null,
          events: [],
          error: null,
        });

        setCreatedAccount((prev) => {
          // Trigger search immediately with the account name
          if (prev.name) {
            setSearchText(prev.name);
            handleSearchAccounts(
              prev.name,
              1,
              searchResults?.currentPayload?.pageSize || DEFAULT_PAGE_SIZE
            );
          }

          return {
            ...prev,
            showNotification: true,
          };
        });
      } else if (data.overall_status === 'failed') {
        const errorMessage =
          data.details || 'Failed to create CRM account. Please try again.';
        setCreateAccountState((prev) => ({
          ...prev,
          isCreating: false,
          error: errorMessage,
        }));
      }
    };

    const handleAccountSalesforceCreateStatusChange = (data: {
      status?: string;
      output?: {
        account?: {
          Id?: string;
          id?: string;
          Name?: string;
          name?: string;
        };
      };
    }) => {
      // Wait for status to be 'processed' AND output to be available
      if (data.status === 'processed' && data.output?.account) {
        const accountId = data.output.account.Id || data.output.account.id;
        const accountName =
          data.output.account.Name || data.output.account.name;

        if (accountId) {
          // Store the created account ID and name for later selection
          // The accountId from Pusher is the Salesforce ID we'll use for matching
          setCreatedAccount((prev) => {
            const updated = {
              id: accountId,
              name: accountName || prev.name || null,
              showNotification: prev.showNotification,
              salesforceId: accountId, // Store as salesforceId for matching
            };

            // If we have the name but search hasn't been triggered yet, trigger it now
            // This handles the case where this event fires before handleProcessStatusChange
            if (updated.name && !prev.salesforceId) {
              setSearchText(updated.name);
              handleSearchAccounts(
                updated.name,
                1,
                searchResults?.currentPayload?.pageSize || DEFAULT_PAGE_SIZE
              );
            }

            return updated;
          });
        }
      }
    };

    subscription.bind(
      'crm-workflow-process-status-changed',
      handleProcessStatusChange
    );

    const eventName = 'crm-account-salesforce-create-status-changed';
    subscription.bind(eventName, handleAccountSalesforceCreateStatusChange);

    return () => {
      subscription.unbind(
        'crm-workflow-process-status-changed',
        handleProcessStatusChange
      );
      subscription.unbind(eventName, handleAccountSalesforceCreateStatusChange);
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    createAccountState.channel,
    createAccountState.events,
    handleSearchAccounts,
    searchResults?.currentPayload?.pageSize,
    DEFAULT_PAGE_SIZE,
  ]);
};

export const useDeleteEntityLink = ({
  deleteState,
  setDeleteState,
  onDeleteSuccess,
  toggleOpenModal,
}: UseDeleteEntityLinkParams) => {
  // Subscribe to Pusher channel for DELETE completion
  useEffect(() => {
    if (!deleteState.deleteChannel || !deleteState.deleteEvents.length) {
      return;
    }

    const subscription = subscribe(deleteState.deleteChannel);

    const handleDeleteStatusChange = (data: {
      overall_status?: string;
      details?: string;
    }) => {
      if (data.overall_status === 'processed') {
        setDeleteState((prev) => ({
          ...prev,
          deleteChannel: null,
          deleteEvents: [],
          deleteComplete: true,
        }));
      } else if (data.overall_status === 'failed') {
        setDeleteState((prev) => ({
          ...prev,
          isDeleting: false,
          deleteChannel: null,
          deleteEvents: [],
          deleteComplete: false,
          deleteError:
            data.details || 'Failed to delete entity link. Please try again.',
        }));
      }
    };

    // Find the process status event from the events array
    const processEvent = deleteState.deleteEvents.find(
      (event) => event.name === 'crm-workflow-process-status-changed'
    );

    if (processEvent) {
      subscription.bind(processEvent.name, handleDeleteStatusChange);
    }

    return () => {
      if (processEvent) {
        subscription.unbind(processEvent.name, handleDeleteStatusChange);
      }
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteState.deleteChannel, deleteState.deleteEvents]);

  // Complete when DELETE is done
  useEffect(() => {
    if (!deleteState.isDeleting) return;

    if (deleteState.deleteComplete) {
      setDeleteState((prev) => ({
        ...prev,
        isDeleting: false,
        deleteComplete: false,
      }));
      onDeleteSuccess?.();
      toggleOpenModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deleteState.deleteComplete,
    deleteState.isDeleting,
    onDeleteSuccess,
    toggleOpenModal,
  ]);
};
